import { Injectable, inject, signal } from '@angular/core';
import { Firestore, doc, getDoc } from '@angular/fire/firestore';

export interface PromoConfig {
	isLive: boolean;
	management: {
		promoStartDate: string;
		promoEndDate: string;
		prizes: {
			toggleDailyPrizeBatchRun: boolean;
			spinToWinRate: number;
			avgDailyWinCountEstimate: number;
		};
	};
}

@Injectable({
	providedIn: 'root'
})
export class PromoStatusService {
	private firestore = inject(Firestore);

	// Signals for reactive state management
	public promoConfig = signal<PromoConfig | null>(null);
	public isPromoActive = signal<boolean>(true);
	public isLoading = signal<boolean>(false);
	public error = signal<string | null>(null);

	constructor() {
		this.loadPromoConfig();
	}

	/**
	 * Load promo configuration from Firestore and check if promotion is active
	 */
	async loadPromoConfig(): Promise<void> {
		this.isLoading.set(true);
		this.error.set(null);

		try {
			const promoConfigDocRef = doc(this.firestore, 'promo', 'config');
			const promoConfigDoc = await getDoc(promoConfigDocRef);

			if (!promoConfigDoc.exists()) {
				this.error.set('Promo configuration not found');
				this.isPromoActive.set(false);
				return;
			}

			const config = promoConfigDoc.data() as PromoConfig;
			this.promoConfig.set(config);

			// Check if promotion is active
			const isActive = this.checkPromoActive(config);
			this.isPromoActive.set(isActive);

		} catch (error) {
			console.error('Error loading promo config:', error);
			this.error.set('Loading Error');
			this.isPromoActive.set(false);
		} finally {
			this.isLoading.set(false);
		}
	}

	/**
	 * Check if promotion is currently active based on isLive flag and date range
	 * @param config Promo configuration
	 * @returns true if promotion is active, false otherwise
	 */
	private checkPromoActive(config: PromoConfig): boolean {
		// Check if isLive is false
		if (!config.isLive) {
			return false;
		}

		// Check if current time is between promoStartDate and promoEndDate
		// Both dates are parsed with AEST timezone offset, and comparison is done in UTC
		const now = new Date();
		const startDate = new Date(config.management.promoStartDate + 'T00:00:00+11:00'); // AEST
		const endDate = new Date(config.management.promoEndDate + 'T23:59:59+11:00'); // AEST

		return now >= startDate && now <= endDate;
	}

	/**
	 * Get formatted date range for display
	 * @returns Formatted date range string or null if config not loaded
	 */
	getFormattedDateRange(): string | null {
		const config = this.promoConfig();
		if (!config) return null;

		const startDate = new Date(config.management.promoStartDate);
		const endDate = new Date(config.management.promoEndDate);

		return `${startDate.toLocaleDateString('en-AU')} - ${endDate.toLocaleDateString('en-AU')}`;
	}
}
